import { useState } from 'react'
import { Page, PageHeader } from '@/components/page'
import { router } from '@inertiajs/react'
import { WizardGuide, GuideTerm } from '@/components/layout/WizardGuide'
import { WizardLayout } from '../WizardLayout'
import { WizardActions } from '../WizardActions'
import { Button } from '@/components/ui/button'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { firstError, formatCurrency } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { EntityTypeOption, EntityTypeOptionKey, WizardFormationRef } from '@/types'
import { ArrowRight, Clock, HelpCircle, Users, Landmark } from 'lucide-react'
import { HelpMeChooseDialog } from './components/HelpMeChooseDialog'

type Props = {
  formation: WizardFormationRef
  current_entity_type: EntityTypeOptionKey | undefined
  options: EntityTypeOption[]
}

const COPY: Record<EntityTypeOptionKey, { label: string; blurb: string; icon: typeof Users }> = {
  llc: { label: 'LLC', blurb: 'The default for most small businesses — simple, flexible, pass-through taxes.', icon: Users },
  corporation: { label: 'C-Corp', blurb: 'What investors expect if you plan to raise venture funding.', icon: Landmark },
}

export default function EntityTypesEdit({ formation, current_entity_type, options }: Props) {
  const basePath = useBasePath()
  const [selected, setSelected] = useState<EntityTypeOptionKey | ''>(current_entity_type ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)

  const submit = () => {
    if (submitting || !selected) return
    setSubmitting(true)
    setError(null)
    router.patch(`${basePath}/formations/${formation.id}/wizard/entity_type`, { entity_type: selected }, {
      onError: (errors) => setError(firstError(errors.entity_type) ?? 'Something went wrong — try again.'),
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <WizardLayout
      title="LLC or C-Corp?"
      currentStep="entity_type"
      formationId={formation.id}
      resumeStep={formation.current_wizard_step}
      breadcrumbs={[{ label: 'Businesses', href: 'businesses' }, { label: 'LLC or C-Corp?' }]}
      guide={
        <WizardGuide title="LLC or C-Corp">
          <GuideTerm term="LLC">is the default for most small businesses — simple, flexible, pass-through taxes.</GuideTerm>
          <GuideTerm term="C-Corp">is what investors expect if you plan to raise venture funding.</GuideTerm>
        </WizardGuide>
      }
    >
      <Page>
        <PageHeader
          title={<>LLC or C-Corp?</>}
          subtitle={<>Both are legitimate US businesses. Most solo and small-team founders pick LLC; pick C-Corp if you're raising outside investment.</>}
          actions={
            <Button type="button" variant="ghost" size="sm" onClick={() => setHelpOpen(true)} className="text-muted-foreground shrink-0">
              <HelpCircle className="size-3.5" />
              Help me choose
            </Button>
          }
        />

        <form onSubmit={(e) => { e.preventDefault(); submit() }}>
          <RadioGroup value={selected} onValueChange={(v) => setSelected(v as EntityTypeOptionKey)} aria-label="LLC or C-Corp?" className="grid gap-3 sm:grid-cols-2">
            {options.map((option) => {
              const copy = COPY[option.key]
              const Icon = copy.icon
              return (
                <label
                  key={option.key}
                  htmlFor={`entity-type-${option.key}`}
                  className="group flex flex-col gap-3 rounded-lg border p-5 cursor-pointer transition-colors has-data-[state=checked]:border-foreground has-data-[state=checked]:bg-muted hover:border-foreground/40"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="flex items-center gap-2 text-base font-medium text-foreground">
                      <Icon className="size-4 text-muted-foreground" />
                      {copy.label}
                    </span>
                    <RadioGroupItem
                      value={option.key}
                      id={`entity-type-${option.key}`}
                      disabled={submitting}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">{copy.blurb}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    <span className="flex items-center gap-1">
                      <Clock className="size-3" />
                      {option.processing_days_min}-{option.processing_days_max}d estimate
                    </span>
                    <span className="font-medium text-foreground">{formatCurrency(option.total_fee_cents)}</span>
                  </div>
                </label>
              )
            })}
          </RadioGroup>

          {error && <p className="mt-3 text-sm text-destructive" role="alert">{error}</p>}

          <WizardActions currentStep="entity_type" formationId={formation.id} disabled={submitting}>
            <Button type="submit" disabled={submitting || !selected}>
              {submitting ? 'Saving…' : 'Continue'}
              <ArrowRight className="size-4" />
            </Button>
          </WizardActions>
        </form>

        <HelpMeChooseDialog open={helpOpen} onOpenChange={setHelpOpen} onSelect={setSelected} />
      </Page>
    </WizardLayout>
  )
}
